/**
 * @author NikolaV
 */
import { latLonToEN } from '../ol-utils';
import { FeatureMouseInteraction } from '../interaction/FeatureMouseInteraction';
import Feature from 'ol/Feature';
import { Point as OlPoint } from 'ol/geom';
import * as olStyle from 'ol/style';
import { DateUtils } from '@shared/utils/DateUtils';
import { LogpointsService } from '@shared/logpoints/LogpointsService';
//import { WebGLStyle } from 'ol/style/webgl';


const FEATURE_TYPE = 'logpoint';
let lastIndex = 1; // used to assign unique index to each feature when they are added

const styles = {
    hitArea: new olStyle.Style({ // draw behind cross to expand clickable area
        image: new olStyle.Circle({
            fill: new olStyle.Fill({
                color: [0, 0, 0, 0.005],
            }),
            radius: 12,
        }),
    }),
};

let labelTextCache = {}
function getLogpointLabel(logpoint: ILogpoint, labelProps: string[], noNamesInLabel = false): string {
    const cacheKey = `${logpoint.id}-${labelProps.join(',')}-${noNamesInLabel ? '1' : '0'}`;
    if (labelTextCache[cacheKey]) {
        return labelTextCache[cacheKey];
    }

    if (!labelProps.length) return '';

    const isMulti = labelProps.length > 1;
    const showPropertyNames = isMulti && !noNamesInLabel;
    const emptyValue = isMulti && noNamesInLabel ? '/' : '';

    const labels = [];
    for (const labelProp of labelProps) {
        if (labelProp === 'dz') {
            const dx = logpoint.modelDeltaX;
            const dy = logpoint.modelDeltaY;
            const dz = logpoint.modelDeltaZ;
            const mId = logpoint.modelId;

            let dx2 = 0;
            let dy2 = 0;
            if ($.isNumeric(dx) && dx !== 0) {
                dx2 = dx * dx;
            }
            if ($.isNumeric(dy) && dy !== 0) {
                dy2 = dy * dy;
            }

            let value = null;
            if ($.isNumeric(dz)) {
                value = dz.toFixed(3);
                if (dz == 0 && (mId == null || mId < 1)) {
                    value = ''; // not attached to any model. Hide zero dz in this case
                }
                if (dx2 || dy2) {
                    value += ' [' + Math.sqrt(dx2 + dy2).toFixed(3) + 'm]';
                }
            }
            labels.push((showPropertyNames ? 'dz: ' : '') + (value ?? emptyValue));
        }
        else if (labelProp === 'xyz') {
            if (showPropertyNames) {
                labels.push(`x: ${logpoint.x} y: ${logpoint.y} z: ${logpoint.z}`);
            } else {
                labels.push(`${logpoint.x}, ${logpoint.y}, ${logpoint.z}`);
            }
        }
        else if (labelProp === 'date') {
            labels.push(DateUtils.formatDateTime(logpoint.timestamp));
        }
        else if (logpoint.hasOwnProperty(labelProp)) {
            const labelName = LogpointsService.propertyTranslations[labelProp] || labelProp;
            labels.push((showPropertyNames ? labelName + ': ' : '') + (logpoint[labelProp] != null && logpoint[labelProp] != '' ? logpoint[labelProp] : emptyValue));
        }
    }

    labelTextCache[cacheKey] = labels.join('\n');
    return labelTextCache[cacheKey];
}


let labelStyleCache = {}
function renderLabelIconStyle(label: string, color: string): olStyle.Style {
    const cacheKey = `${label}-${color}`;
    if (labelStyleCache[cacheKey]) {
        return labelStyleCache[cacheKey];
    }
    const fontSize = 10;
    const lineHeight = fontSize * 2;
    const labelLines = label.split('\n');
    let longestLine = 0;
    labelLines.forEach(l => {
        if (l.length > longestLine) { longestLine = l.length; }
    });
    const boxWidth = Math.max(3 * fontSize, longestLine * fontSize + (fontSize * 2));

    const canvas = document.createElement('canvas');
    canvas.width = boxWidth;
    canvas.height = 3 * fontSize + (labelLines.length * lineHeight);
    const ctx = canvas.getContext('2d');

    ctx.font = Math.floor(fontSize * 1.6) + 'px Arial, Helvetica, sans serif';
    labelLines.forEach((labelLine, i) => {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.005)';
        ctx.lineWidth = fontSize / 2;
        ctx.strokeText(labelLine, fontSize * 2, fontSize * 2.6 + (i * lineHeight));
        ctx.fillStyle = color;
        ctx.fillText(labelLine, fontSize * 2, fontSize * 2.6 + (i * lineHeight));
    });

    const style = new olStyle.Style({
        image: new olStyle.Icon({
            img: canvas,
            anchorXUnits: 'pixels',
            anchorYUnits: 'pixels',
            anchor: [fontSize , fontSize - 4], // offset the icon so that the logpoint coords at at the center of cross
            scale: 0.75, // looks sharper when scale <1
        }),
    });

    labelStyleCache[cacheKey] = style;
    return style;
}

function renderIconCanvas(logpoint: ILogpoint, center = 8, color: string, hitArea): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = center * 2;
    canvas.height = center * 2;
    const ctx = canvas.getContext('2d');
    const src = logpoint.type.toLowerCase();

    // debug background
    //ctx.fillStyle = 'rgba(255, 165, 0, 0.1)';
    //ctx.strokeRect(0, 0, canvas.width, canvas.height);

    // semi-transparent circle behind cross to expand hit area
    if (hitArea) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.05)'; // 0.05 is minimum for webgl layers
        ctx.beginPath();
        ctx.arc(center, center, canvas.width / 2, 0, 2 * Math.PI, false);
        ctx.fill();
        ctx.closePath();
    }

    // rover gps - basic cross
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(center, 3);
    ctx.lineTo(center, center * 2 - 3);
    ctx.stroke();
    ctx.closePath();

    ctx.beginPath();
    ctx.moveTo(3, center);
    ctx.lineTo(center * 2 - 3, center);
    ctx.stroke();
    ctx.closePath();

    // vehicle - add 45deg line
    if (src == 'vehicle') {
        ctx.beginPath();
        ctx.moveTo(center, center);
        ctx.lineTo(center * 2, 0);
        ctx.stroke();
        ctx.closePath();
    }

    // total station - add circle
    else if (src == 'total_station') {
        ctx.beginPath();
        ctx.arc(center, center, center - 3, 0, 2 * Math.PI, false);
        ctx.stroke();
        ctx.closePath();
    }

    return canvas;
}

function getWebGlStyle(iconScale: number, colorFunction: LogpointColorFunction): any {
    const size = 32;
    const canvas = document.createElement('canvas');
    const spriteMap = [];

    if (colorFunction === 'tolerance' || colorFunction === 'tolerance_approval') {
        canvas.height = size * 5;
        canvas.width = size * 3;
        const ctx = canvas.getContext('2d');

        ['rover_gps', 'vehicle', 'total_station'].forEach((type, i) => {
            ['black', 'red', 'blue', 'green', 'orange'].forEach((color, j) => {
                const x = i * size;
                const y = j * size;
                ctx.drawImage(renderIconCanvas({ type } as any, size / 2, color === 'orange' ? '#FF8D2F' : color, true), x, y);
                spriteMap.push(`${type}_${color}`);
                spriteMap.push([x, y]);
            })
        })
    } else if (colorFunction === 'timestamp_color_ramp') {
        canvas.height = size * colorRampSteps;
        canvas.width = size * 3;
        const ctx = canvas.getContext('2d');

        ['rover_gps', 'vehicle', 'total_station'].forEach((type, i) => {
            for (let j = 0; j < colorRampSteps; j++) {
                const x = i * size;
                const y = j * size;
                const weight = 0.2 + ((j / colorRampSteps) * 0.6);
                ctx.drawImage(renderIconCanvas({ type } as any, size / 2, rgbToHex(colorRamp([255, 0, 0], [0, 0, 255], weight)), true), x, y);
                spriteMap.push(`${type}_${j}`);
                spriteMap.push([x, y]);
            }
        })
    }

    const style: any = {
        filter: ['!=', ['get', 'hidden', 'string'], 'true'],
        'icon-src': canvas.toDataURL(),
        'icon-height': canvas.height,
        'icon-width': canvas.width,
        'icon-size': [size, size],
        'icon-scale': [
            'interpolate',
            ['exponential', 2],
            ['zoom'],
            5,
            0.3,
            13,
            iconScale,
        ],
        'icon-offset': [
            'match',
            ['get', 'type_and_color'],
            ...spriteMap,
            [0, 0],
        ],
    };
    return style;
}

function getIconSpriteKey(logpoint: ILogpoint, colorFunction: LogpointColorFunction, minTimestamp?: number, maxTimestamp?: number) {
    let type = logpoint.type;
    if (type === 'UNKNOWN' || type === 'MANUAL') {
        type = 'ROVER_GPS';
    }

    if (colorFunction === 'tolerance_approval') {
        return (type + '_' + colorFunctions.tolerance_approval(logpoint)).toLowerCase();
    }
    if (colorFunction === 'tolerance') {
        return (type + '_' + colorFunctions.tolerance(logpoint)).toLowerCase();
    }
    if (colorFunction === 'timestamp_color_ramp') {
        return (type + '_' + colorFunctions.timestamp_color_ramp_index(logpoint, minTimestamp, maxTimestamp)).toLowerCase();
    }
}


function makeFeature(logpoint: ILogpoint, mouseInteraction?: FeatureMouseInteraction): Feature {
    const point = latLonToEN(logpoint.lat, logpoint.lon);
    if (!point) {
        console.warn('Skipping bad logpoint: ', logpoint);
        return null;
    }

    const source: FeatureSource = {
        modelId: logpoint.modelId,
    };
    if (logpoint.folderId) source.folderId = logpoint.folderId;
    if (logpoint.vehicleId) source.vehicleId = logpoint.vehicleId;
    if (logpoint.alignmentId) source.alignmentId = logpoint.alignmentId;

    const feature = new Feature({
        geometry: new OlPoint(point),
        type: FEATURE_TYPE,
        data: logpoint,
        source: source,
        index: lastIndex++,
        no_hover_style: true,
        type_and_color: null,
        mouseInteraction: mouseInteraction
    });

    feature.setId(logpoint.id);
    return feature;
}

function recomputeFeatureProps(feature: Feature, colorFunction: LogpointColorFunction, minTimestamp?: number, maxTimestamp?: number): void {
    const key = getIconSpriteKey(feature.get('data'), colorFunction, minTimestamp, maxTimestamp);
    feature.set('type_and_color', key, true)
}

const colorRampSteps = 16;
const colorFunctions = {
    timestamp_color_ramp: (l: ILogpoint, minTimestamp: number, maxTimestamp: number) => {
        const safeTimestamp = utils.clamp(minTimestamp, l.timestamp, maxTimestamp);
        const weightRaw = (safeTimestamp - minTimestamp) / (maxTimestamp - minTimestamp);
        const weight = parseFloat(weightRaw.toFixed(2));
        return rgbToHex(colorRamp([255, 0, 0], [0, 0, 255], weight));
    },
    timestamp_color_ramp_index: (l: ILogpoint, minTimestamp: number, maxTimestamp: number) => { // return color as 0-63 index
        const safeTimestamp = utils.clamp(minTimestamp, l.timestamp, maxTimestamp);
        const weightRaw = (safeTimestamp - minTimestamp) / (maxTimestamp - minTimestamp);
        const weight = parseFloat(weightRaw.toFixed(2));
        return Math.floor(weight*colorRampSteps);
    },
    tolerance: (l: ILogpoint) => {
        if (l.dzInTolerance == 0) {
            return 'black';
        }
        else if (l.dzInTolerance == 4) {
            return 'blue';
        }
        else {
            return 'red';
        }
    },
    tolerance_approval: (l: ILogpoint) => {
        if (typeof (l.approvalStage) === 'undefined' || l.approvalStage === null ||  // no approvals
            l.approvalStage === 'MEASURED') {
            if (l.dzInTolerance == 0) {
                return 'black';
            }
            else if (l.dzInTolerance == 4) {
                return 'blue';
            }
            else {
                return 'red';
            }
        }
        else {
            if ('APPROVED' === l.approvalStage) {
                return 'green';
            }
            if ('CHECKED' === l.approvalStage) {
                return 'orange';
            }
        }
    }
};

function colorRamp(color1: number[], color2: number[], weight: number) {
    const w1 = weight;
    const w2 = 1 - w1;
    return [Math.round(color1[0] * w1 + color2[0] * w2),
        Math.round(color1[1] * w1 + color2[1] * w2),
        Math.round(color1[2] * w1 + color2[2] * w2)];
}

function componentToHex(c) {
    const hex = c.toString(16);
    return hex.length == 1 ? '0' + hex : hex;
}

function rgbToHex(rgb) {
    return '#' + componentToHex(rgb[0]) + componentToHex(rgb[1]) + componentToHex(rgb[2]);
}

function clearCache() {
    labelStyleCache = {};
    labelTextCache = {};
}

export const LogpointFeatureFactory = {
    makeFeature,
    getLogpointLabel,
    renderIconCanvas,
    renderLabelIconStyle,
    getWebGlStyle,
    colorFunctions,
    styles,
    recomputeFeatureProps,
    clearCache,
    FEATURE_TYPE,
};
